import React, { useEffect, useState } from 'react';
import { Box, Button, TextField, Typography } from '@material-ui/core';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { GET_ONBOARDING_OPTION_STAGES } from '../../../../../common/onboarding-option-task/onboarding-option-stages-model.gql';
import { GetOnboardingOptionStages } from '../../../../../common/onboarding-option-task/__generated__/GetOnboardingOptionStages';

interface OnboardingOptionContentFormProps {
  optionType: string;
  optionLabel: string;
  defaultTitle: string;
  defaultDescription: string;
  enabled: boolean;
}

export const OnboardingOptionContentForm: React.FC<OnboardingOptionContentFormProps> = ({
  optionType,
  optionLabel,
  defaultTitle,
  defaultDescription,
  enabled,
}) => {
  const apiUrl = useApiUrl();
  const { setSnackbar } = useSnackbar();
  const contentMutation = useCampfireFetch({ defer: true });

  const { data, refetch } = useCampfireQuery<GetOnboardingOptionStages, {}>(GET_ONBOARDING_OPTION_STAGES);

  const current = data?.vm.onboardingOptionStages.find((stage) => stage.optionType === optionType);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    setTitle(current?.customTitle || '');
    setDescription(current?.customDescription || '');
  }, [current?.customTitle, current?.customDescription]);

  const isDirty = title !== (current?.customTitle || '') || description !== (current?.customDescription || '');

  const handleSave = async () => {
    try {
      const response = await contentMutation.run({
        url: `${apiUrl}/vm/admin-console/onboarding-option-stage/content`,
        method: 'post',
        data: {
          optionType,
          customTitle: title.trim() === '' ? null : title,
          customDescription: description.trim() === '' ? null : description,
        },
      });

      if (!response.ok) {
        throw new Error('Request failed');
      }

      setSnackbar({ variant: 'success', open: true, message: `${optionLabel} wording saved` });
      refetch();
    } catch (error) {
      setSnackbar({ variant: 'error', open: true, message: `Couldn't save ${optionLabel} wording` });
    }
  };

  const handleReset = () => {
    setTitle('');
    setDescription('');
  };

  if (!enabled) {
    return null;
  }

  return (
    <Box style={{ marginTop: '20px', marginLeft: '16px' }}>
      <Typography variant='subtitle2' style={{ fontSize: '14px', color: '#666', marginBottom: '4px', fontWeight: 600 }}>
        Wording
      </Typography>
      <Typography style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
        Replace the default heading and description volunteers see on this step. Leave blank to use the Volaby wording.
      </Typography>

      <Box display='flex' flexDirection='column' style={{ gap: '12px', maxWidth: '560px' }}>
        <TextField
          fullWidth
          size='small'
          variant='outlined'
          label='Heading'
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={defaultTitle}
          inputProps={{ maxLength: 255 }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          fullWidth
          multiline
          rows={3}
          size='small'
          variant='outlined'
          label='Description'
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={defaultDescription}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      <Box display='flex' alignItems='center' style={{ gap: '8px', marginTop: '12px' }}>
        <Button
          variant='contained'
          color='primary'
          disableElevation
          size='small'
          disabled={!isDirty || contentMutation.isLoading}
          onClick={handleSave}
        >
          Save wording
        </Button>
        {(current?.customTitle || current?.customDescription) && (
          <Button size='small' disabled={contentMutation.isLoading} onClick={handleReset}>
            Reset to default
          </Button>
        )}
      </Box>
    </Box>
  );
};
